// src/services/utils/supabaseQueryWrapper.js
// Eliminates repetitive try/catch + error-check boilerplate across all services.
//
// Before (15 lines per method):
//   try {
//     logger.debug("Loading bays for garage:", garageId);
//     const { data, error } = await supabase.from("garage_bays").select("*").eq("garage_id", garageId);
//     if (error) { logger.error("Error fetching bays:", error); return createErrorResponse(error.message); }
//     logger.success(`Loaded ${data?.length || 0} bays`);
//     return createSuccessResponse(data || []);
//   } catch (error) {
//     logger.error("Error in getGarageBays:", error);
//     return createErrorResponse("Failed to fetch garage bays");
//   }
//
// After (1 line):
//   return executeQuery(supabase.from("garage_bays").select("*").eq("garage_id", garageId), { context: "Loading bays", defaultValue: [] });

import {
  createSuccessResponse,
  createErrorResponse,
  logger,
} from "../../Utils/ErrorHandler";

/**
 * Execute a Supabase query with standardized error handling, logging, and response formatting.
 *
 * @param {Object|Function} queryOrFn - Supabase query builder, or a function that returns one (for conditional queries)
 * @param {string|Object} options - Context string, or options object
 * @param {string} options.context - Description for logging (e.g., "Loading bays")
 * @param {*} [options.defaultValue=null] - Default value when data is null (use [] for list queries)
 * @param {string} [options.errorMessage] - Custom error message for unexpected errors
 * @param {boolean} [options.returnCount=false] - Return count instead of data (for count queries)
 * @param {boolean} [options.single=false] - Expect a single row (handles PGRST116 as "not found")
 * @param {string} [options.notFoundMessage="Not found"] - Error message when single row not found
 * @returns {Promise<{success: boolean, data: *, error: string|null}>}
 *
 * @example
 * // Simple select (returns array)
 * return executeQuery(
 *   supabase.from("garage_bays").select("*").eq("garage_id", garageId),
 *   { context: "Loading bays", defaultValue: [] }
 * );
 *
 * @example
 * // Conditional query (use function form)
 * return executeQuery(
 *   () => {
 *     let q = supabase.from("garage_bays").select("*").eq("garage_id", garageId);
 *     if (!includeInactive) q = q.eq("is_active", true);
 *     return q;
 *   },
 *   { context: "Loading bays", defaultValue: [] }
 * );
 *
 * @example
 * // Single row lookup
 * return executeQuery(
 *   supabase.from("garage_bays").select("*").eq("id", bayId).single(),
 *   { context: "Loading bay", single: true, notFoundMessage: "Bay not found" }
 * );
 *
 * @example
 * // Count query
 * return executeQuery(
 *   supabase.from("customer_notes").select("id", { count: "exact", head: true }).eq("customer_id", customerId),
 *   { context: "Counting notes", returnCount: true }
 * );
 *
 * @example
 * // Short form (context string only)
 * return executeQuery(
 *   supabase.from("bay_capabilities").select("*").order("display_order"),
 *   "Loading bay capabilities"
 * );
 */
export const executeQuery = async (queryOrFn, options = {}) => {
  const opts =
    typeof options === "string" ? { context: options } : options;

  const {
    context = "Query",
    defaultValue = null,
    errorMessage,
    returnCount = false,
    single = false,
    notFoundMessage = "Not found",
  } = opts;

  const fallbackError = errorMessage || `Failed: ${context.toLowerCase()}`;

  try {
    logger.debug(context);

    const query = typeof queryOrFn === "function" ? queryOrFn() : queryOrFn;
    const result = await query;
    const { data, error, count } = result;

    if (error) {
      // Handle "row not found" for .single() queries
      if (single && error.code === "PGRST116") {
        return createErrorResponse(notFoundMessage, null);
      }
      logger.error(`Error - ${context}:`, error);
      return createErrorResponse(error.message);
    }

    if (returnCount) {
      logger.success(`${context}: ${count ?? 0}`);
      return createSuccessResponse(count ?? 0);
    }

    const value = data ?? defaultValue;
    logger.success(context);
    return createSuccessResponse(value);
  } catch (error) {
    logger.error(`Error in ${context}:`, error);
    return createErrorResponse(fallbackError);
  }
};
